// ─────────────────────────────────────────────
//  AssignmentRow — one always-editable assignment row (Phase 4 D-01, D-09)
//  Sheet 7 columns in order: OR | Start | SURGEON | CRNA | RELIEF |
//  MD/DO | RELIEF, plus an Actions column with Save at the end.
//  Every room row renders its dropdowns permanently (no Edit step) so the
//  whole day can be filled in row by row and saved per row.
//  The row owns its draft state: it seeds from the assignment doc, marks
//  itself dirty on any change, and re-seeds from live snapshots only while
//  clean — so background updates never clobber in-progress typing.
//  Cell renderers extracted to assignmentRow.cells.js (keeps this file < 500 lines).
//  Template rows (isTemplateLocked) show the OR label read-only; free rows
//  allow editing the OR cell. Desktop renders a <tr>; mobile renders a Card.
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import tokens from "../theme/tokens";
import Button from "./Button";
import Card from "./Card";
import { buildEditCells } from "./assignmentRow.cells";

const { color, font, space } = tokens;

const EMPTY_ROW = {
  orRoom: "",
  surgeon: "",
  procedure: "",
  startTime: "",
  endTime: "",
  attendingId: "",
  ahpId: "",
  ahpDepartTime: "",
  crnaReliefId: "",
  mdReliefId: "",
};

// Seed the local draft from the live doc (or an empty template slot).
const seedFrom = (assignment, templateOr) => ({
  ...EMPTY_ROW,
  ...(assignment || {}),
  orRoom: assignment?.orRoom || templateOr || "",
});

export default function AssignmentRow({
  assignment,
  doctors = [],
  crnas = [],
  surgeons = [],
  assignedAhpIds,           // Set of ahpIds booked on other saved rows (double-book guard)
  assignedAttendingIds,     // Set of attendingIds booked on other saved rows
  onSaveRow,                // async (rowData, existingId) — persists the row
  onRemove,
  isMobile = false,
  // Template support
  templateOr = null,        // OR label for template rows (pre-filled, locked for template)
  isTemplateLocked = false, // true = template row; OR cell is read-only
  onMemberPatch,            // (listKey, memberId, patch) → persists per-day member field
}) {
  const a = assignment || null;
  const [local, setLocal] = useState(() => seedFrom(a, templateOr));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Live snapshot updates re-seed the row only while it has no unsaved edits.
  // Deliberately keyed on the incoming doc only — dirty/saving guard reads
  // current state without retriggering the effect.
  useEffect(() => {
    if (dirty || saving) return;
    setLocal(seedFrom(a, templateOr));
  }, [assignment, templateOr]); // eslint-disable-line

  const attending = doctors.find((d) => d.id === local.attendingId);
  const ahp = crnas.find((c) => c.id === local.ahpId);

  const change = (field, value) => {
    setDirty(true);
    setLocal((prev) => ({ ...prev, [field]: value }));
  };
  const set = (field) => (e) => change(field, e.target.value);

  const handleShiftCodeChange = (e) => {
    const val = e.target.value;
    if (local.ahpId && onMemberPatch) {
      onMemberPatch("crnas", local.ahpId, { shiftCode: val === "" ? null : val });
    }
  };

  const handleCrnaChange = (e) => {
    change("ahpId", e.target.value);
    const selected = crnas.find((c) => c.id === e.target.value);
    if (selected?.shiftCode && onMemberPatch) {
      onMemberPatch("crnas", e.target.value, { shiftCode: selected.shiftCode });
    }
  };

  const handleCallOrderChange = (e) => {
    const val = Number(e.target.value);
    if (local.attendingId && onMemberPatch) {
      onMemberPatch("supervisors", local.attendingId, { callOrder: val });
    }
  };

  const handleSave = async () => {
    if (!onSaveRow) return;
    setSaving(true);
    setError("");
    try {
      await onSaveRow(local, a?.id ?? null);
      setDirty(false);
    } catch (err) {
      console.error("Save assignment row failed:", err);
      setError("Couldn't save. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const cells = buildEditCells({
    a: local, saving, set, crnas, doctors, ahp, attending,
    handleShiftCodeChange, handleCrnaChange, handleCallOrderChange,
    isTemplateLocked, templateOr, surgeons,
    assignedAhpIds, assignedAttendingIds,
  });

  const actions = (
    <span style={st.actionsWrap}>
      <Button size="sm" onClick={handleSave} loading={saving} ariaLabel="Save assignment">
        Save
      </Button>
      {onRemove && (
        <Button size="sm" variant="danger" onClick={onRemove} disabled={saving} ariaLabel="Remove assignment">
          Remove
        </Button>
      )}
    </span>
  );

  const rowBg = error ? "#fef2f2" : dirty ? "#e8edf9" : "transparent";
  const errorLine = error ? <div style={st.errorText}>{error}</div> : null;
  const savingLine = saving ? <div style={st.savingText}>Saving…</div> : null;

  if (isMobile) {
    return (
      <Card style={{
        marginBottom: space.sm,
        background: rowBg === "transparent" ? color.surface.card : rowBg,
        ...(dirty ? { borderLeft: `3px solid ${color.accent.primary}` } : null),
      }}>
        <div style={st.cardHeader}>
          <span style={st.cardTitle}>
            OR {isTemplateLocked ? (templateOr || local.orRoom || "—") : ""}
          </span>
          {!isTemplateLocked && cells.orRoom}
        </div>
        <div style={st.cardField}><span style={st.cardLabel}>Start</span>{cells.startTime}</div>
        <div style={st.cardField}><span style={st.cardLabel}>End</span>{cells.endTime}</div>
        <div style={st.cardField}><span style={st.cardLabel}>Surgeon</span>{cells.surgeon}</div>
        <div style={st.cardField}><span style={st.cardLabel}>CRNA</span>{cells.crna}</div>
        <div style={st.cardField}><span style={st.cardLabel}>MD/DO</span>{cells.attending}</div>
        {savingLine}
        {errorLine}
        <div style={{ display: "flex", gap: space.sm, marginTop: space.sm }}>{actions}</div>
      </Card>
    );
  }

  return (
    <tr style={{
      background: rowBg,
      ...(dirty ? { borderLeft: `3px solid ${color.accent.primary}` } : null),
      opacity: saving ? 0.7 : 1,
    }}>
      {/* Column order: OR | Start | End | SURGEON | CRNA | MD/DO | Actions */}
      <td style={st.td}>{cells.orRoom}</td>
      <td style={st.td}>{cells.startTime}</td>
      <td style={st.td}>{cells.endTime}</td>
      <td style={st.td}>{cells.surgeon}</td>
      <td style={st.td}>{cells.crna}</td>
      <td style={st.td}>{cells.attending}</td>
      <td style={st.td}>
        {actions}
        {savingLine}
        {errorLine}
      </td>
    </tr>
  );
}

const st = {
  td: {
    padding: `3px ${space.xs}px`,
    borderBottom: `1px solid ${color.border.default}`,
    fontFamily: font.family,
    fontSize: font.size.label,
    color: color.text.primary,
    verticalAlign: "middle",
  },
  actionsWrap: {
    display: "inline-flex",
    gap: space.xs,
    alignItems: "center",
  },
  errorText: {
    color: color.accent.error,
    fontSize: font.size.label,
    marginTop: space.xs,
  },
  savingText: {
    color: color.text.muted,
    fontSize: font.size.label,
    marginTop: space.xs,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: space.sm,
  },
  cardTitle: {
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    color: color.text.primary,
  },
  cardField: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginBottom: space.sm,
  },
  cardLabel: {
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
};
